import React from "react";
import { useAtom, useSetAtom } from "jotai";
import moment from "moment";

import { useModal, InfoModal, LoadingModal, ErrorModal } from "components/Modal";

import { getErrorMessage } from "services/api";

import { DocumentsData } from "constant-data/documentsData";

import {
  docItemsAtom,
  docItemsToDeleteAtom,
  documentsAtom,
  documentsFiltersAtom,
  documentsFiltersInit,
  openedDocItemAtom,
  openedDocItemStateAtom,
  openedDocumentAtom,
  openedDocumentStateAtom,
  selectedDocItemAtom,
  selectedDocumentAtom,
  selectedDocumentTypeAtom,
  selectedDocumentTypeTemplatesAtom,
} from "atoms/documentsAtom";

import { DocumentModal } from "features/DocumentModal";

import * as api from "./api";

/** Преобразование позиции к payload для create/update */
const docItemToPayload = (docItem, docType) => {
  const payload = {
    ...docItem,
    isNew: undefined,
    isEdited: undefined,
    index: undefined,
    item: undefined,
    item2: undefined,
    measure: undefined,
    measure2: undefined,
    pricelist: undefined,
    itemid: docItem.item?.itemid,
    item2id: docItem.item2?.itemid,
    measureid: docItem.measure?.measureid,
    measure2id: docItem.measure2?.measureid,
    pricelistid: docItem.pricelist?.pricelistid,
  };

  // Инвентаризация не отправляет второстепенные поля
  if (Number(docType) === 8) {
    payload.item2id = undefined;
    payload.measure2id = undefined;
    payload.pricelistid = undefined;
  }

  return payload;
};

const useDocuments = () => {
  const { openModal, openModal2, closeModal, closeModal2 } = useModal();

  const [selectedDocumentType] = useAtom(selectedDocumentTypeAtom);
  const setSelectedDocumentTypeTemplates = useSetAtom(
    selectedDocumentTypeTemplatesAtom
  );
  const [documentsFilters, setDocumentsFilters] = useAtom(documentsFiltersAtom);
  const [documents, setDocuments] = useAtom(documentsAtom);
  const [selectedDocument, setSelectedDocument] = useAtom(selectedDocumentAtom);
  const [openedDocument, setOpenedDocument] = useAtom(openedDocumentAtom);
  const [openedDocumentState, setOpenedDocumentState] = useAtom(
    openedDocumentStateAtom
  );
  const [docItems, setDocItems] = useAtom(docItemsAtom);
  const [selectedDocItem, setSelectedDocItem] = useAtom(selectedDocItemAtom);
  const [openedDocItem, setOpenedDocItem] = useAtom(openedDocItemAtom);
  const [openedDocItemState, setOpenedDocItemState] = useAtom(
    openedDocItemStateAtom
  );
  const [docItemsToDelete, setDocItemsToDelete] = useAtom(docItemsToDeleteAtom);

  const showError2 = (e, title) =>
    openModal2(
      <ErrorModal title={title} message={getErrorMessage(e)} onClose={closeModal2} />
    );

  /* ================= документы ================= */

  const getAndSavePrintTemplates = async (docOperationId) => {
    try {
      const { items } = await api.getPrintTemplates({
        doc_operation: docOperationId,
        page: 1,
        pageSize: 500,
      });
      setSelectedDocumentTypeTemplates(items);
    } catch (e) {
      setSelectedDocumentTypeTemplates([]);
    }
  };

  const initDocumentState = (doc) => {
    setOpenedDocumentState(
      doc
        ? {
            docid: doc.docid,
            time: new Date(doc.time),
            comment: doc.comment,
            storeid: doc.storeid,
            store2id: doc.store2id,
            priceupdated: Boolean(doc.priceupdated),
          }
        : {}
    );
  };

  const getDocItemsForDoc = async (docid) => {
    const { items } = await api.getDocItems({ docid });
    setDocItems(
      items.map((item, index) => ({
        ...item,
        index: items.length - index,
      }))
    );
  };

  const openDocumentModal = (doc) => {
    setOpenedDocument(doc);
    initDocumentState(doc);
    setDocItems([]);
    setDocItemsToDelete([]);
    setSelectedDocItem(null);
    openModal(<DocumentModal />);
  };

  const openDocument = (doc) => {
    const target = doc || selectedDocument;
    if (!target) return;
    setSelectedDocument(target);
    openDocumentModal({ ...target });
    getDocItemsForDoc(target.docid).catch((e) => showError2(e));
  };

  const createDocumentAndOpen = async () => {
    if (!selectedDocumentType?.id) return;
    try {
      const doc = await api.createDocument({
        operation_id: selectedDocumentType.id,
      });
      setDocumentsFilters((prev) => ({
        ...prev,
        dateTo: moment().endOf("day").toDate(),
      }));
      setSelectedDocument(doc);
      openDocumentModal(doc);
    } catch (e) {
      showError2(e, "Не удалось создать документ");
    }
  };

  const deleteDocument = async () => {
    if (!selectedDocument) return;
    try {
      await api.deleteDocuments({ docids: [selectedDocument.docid] });
      setDocuments(documents.filter((d) => d.docid !== selectedDocument.docid));
      setSelectedDocument(null);
      closeModal();
    } catch (e) {
      closeModal();
      showError2(e, "Не удалось удалить документ");
    }
  };

  const conductDocument = async () => {
    if (!selectedDocumentType?.id || !selectedDocument) return;
    const { shouldCheck2Stores } = DocumentsData[selectedDocumentType.id] || {};

    if (
      !selectedDocument.storeid ||
      (shouldCheck2Stores && !selectedDocument.store2id)
    ) {
      return openModal(
        <InfoModal
          title={
            shouldCheck2Stores
              ? "Проводить можно только документы с выбранными складами"
              : "Проводить можно только документы с выбранным складом"
          }
          onClose={closeModal}
        />
      );
    }

    openModal(<LoadingModal title="Проведение документа..." />);
    try {
      const res = await api.conductDocuments({
        docids: [selectedDocument.docid],
      });

      if (res?.errorMessages?.length) {
        openModal(
          <InfoModal
            title={`Возникли следующие ошибки:\n${res.errorMessages.join("\n")}`}
            onClose={closeModal}
          />
        );
      } else {
        setDocuments(
          documents.map((d) =>
            d.docid === selectedDocument.docid ? { ...d, ok: 1 } : d
          )
        );
        setSelectedDocument(null);
        closeModal();
      }
    } catch (e) {
      closeModal();
      showError2(e, "Не удалось провести документ");
    }
  };

  const unConductDocument = async () => {
    if (!selectedDocument) return;
    try {
      await api.unconductDocuments({ docids: [selectedDocument.docid] });
      setDocuments(
        documents.map((d) =>
          d.docid === selectedDocument.docid ? { ...d, ok: 0 } : d
        )
      );
      setSelectedDocument(null);
      closeModal();
    } catch (e) {
      closeModal();
      showError2(e, "Не удалось отменить проведение");
    }
  };

  /* ================= сохранение документа ================= */

  const createNewDocItems = async () => {
    const toCreate = docItems
      .filter((i) => i.isNew)
      .map((i) => ({
        ...docItemToPayload(i, openedDocument.type),
        docitemid: undefined,
      }));

    if (toCreate.length) await api.createDocItems({ docitems: toCreate });
  };

  const updateEditedDocItems = async () => {
    const toUpdate = docItems
      .filter((i) => i.isEdited && !i.isNew)
      .map((i) => docItemToPayload(i, openedDocument.type));

    if (toUpdate.length) await api.updateDocItems({ docitems: toUpdate });
  };

  const deleteMarkedDocItems = async () => {
    if (!docItemsToDelete.length) return;
    await api.deleteDocItems({
      docitemids: docItemsToDelete.map((i) => i.docitemid),
      docid: docItemsToDelete[0].docid,
    });
    setDocItemsToDelete([]);
  };

  const updateDocument = async (shouldCloseAndConduct) => {
    if (!openedDocument) return;
    const isConducting =
      Boolean(shouldCloseAndConduct) && Boolean(openedDocumentState.storeid);

    openModal2(<LoadingModal title="Сохранение..." />);

    try {
      await createNewDocItems();
      await deleteMarkedDocItems();
      await updateEditedDocItems();

      const res = await api.updateDocument({
        docid: openedDocument.docid,
        operation_id: openedDocument.type,
        storeid: openedDocumentState.storeid,
        store2id: openedDocumentState.store2id,
        time: openedDocumentState.time,
        comment: openedDocumentState.comment,
        priceupdated: openedDocumentState.priceupdated ? 1 : 0,
        shouldConduct: isConducting,
      });

      if (!shouldCloseAndConduct) {
        await getDocItemsForDoc(openedDocument.docid);
      }

      if (res?.docid) {
        setDocuments(
          documents.map((d) => (d.docid === res.docid ? { ...d, ...res } : d))
        );
        setSelectedDocument((prev) =>
          prev ? { ...prev, ...res, number: prev.number } : prev
        );
        setOpenedDocument(res);
        initDocumentState(res);
      }

      openModal2(
        <InfoModal
          title="Изменения были успешно сохранены"
          onClose={closeModal2}
          onOkClick={() => {
            closeModal2();
            if (shouldCloseAndConduct) closeDocumentModal();
          }}
        />
      );
    } catch (e) {
      showError2(e, "Не удалось сохранить документ");
    }
  };

  /* ================= позиции ================= */

  const buildDocItemFromState = (referenceData) => {
    const { measureUnits = [], priceLists = [] } = referenceData;
    const state = openedDocItemState;

    // Полные объекты товаров приходят из формы (серверный поиск),
    // либо из редактируемой позиции
    const findItem = (id) =>
      (state.itemData && String(state.itemData.itemid) === String(id)
        ? state.itemData
        : null) ||
      (state.item2Data && String(state.item2Data.itemid) === String(id)
        ? state.item2Data
        : null) ||
      (openedDocItem?.item &&
      String(openedDocItem.item.itemid) === String(id)
        ? openedDocItem.item
        : null) ||
      (openedDocItem?.item2 &&
      String(openedDocItem.item2.itemid) === String(id)
        ? openedDocItem.item2
        : null);

    return {
      item: state.itemid ? findItem(state.itemid) : null,
      item2: state.item2id ? findItem(state.item2id) : null,
      measure:
        measureUnits.find(
          (m) => String(m.measureid) === String(state.measureid)
        ) || null,
      measure2:
        measureUnits.find(
          (m) => String(m.measureid) === String(state.measure2id)
        ) || null,
      pricelist:
        priceLists.find(
          (p) => String(p.pricelistid) === String(state.pricelistid)
        ) || null,
      price: state.price,
      realamount: state.realamount,
      amount: state.amount || 0,
      difference:
        (Number(state.amount) || 0) - (Number(state.realamount) || 0) || 0,
      total: state.total,
      salepercent: state.salepercent,
      frontprice: state.frontprice,
      itemcomment: state.itemcomment,
    };
  };

  const saveDocItem = (referenceData) => {
    const payload = buildDocItemFromState(referenceData);

    if (openedDocItem) {
      const updated = { ...openedDocItem, ...payload, isEdited: true };
      setDocItems(
        docItems.map((i) =>
          i.docitemid === updated.docitemid ? updated : i
        )
      );
    } else {
      const created = {
        ...payload,
        docid: openedDocument.docid,
        isNew: true,
        docitemid: `new-${Math.random()}`,
        index: docItems.length + 1,
      };
      setDocItems([created, ...docItems]);
    }

    closeDocItemModal();
  };

  /** Массовое добавление позиций (заполнение по остатку) */
  const createManyDocItems = (items, measureUnits) => {
    const newDocItems = items.map((item, i) => ({
      docid: openedDocument.docid,
      isNew: true,
      docitemid: `new-${Math.random()}`,
      index: docItems.length + 1 + i,
      item,
      measure:
        measureUnits.find(
          (m) => String(m.measureid) === String(item.measureid)
        ) || null,
      price: 0,
      realamount: item.realamount,
      amount: 0,
      difference: -(item.realamount || 0),
      total: 0,
    }));

    setDocItems([...newDocItems.reverse(), ...docItems]);
  };

  const removeDocItem = () => {
    if (!selectedDocItem) return;
    setDocItems(
      docItems.filter((i) => i.docitemid !== selectedDocItem.docitemid)
    );
    if (!selectedDocItem.isNew) {
      setDocItemsToDelete([...docItemsToDelete, selectedDocItem]);
    }
    setSelectedDocItem(null);
    closeModal2();
  };

  const duplicateDocument = async (option) => {
    if (!option || !openedDocument) return;
    try {
      const doc = await api.duplicateDocument({
        docid: openedDocument.docid,
        newDocType: option.value,
      });
      openModal2(
        <InfoModal
          title={`Новый документ ${option.label.toLowerCase()} по номеру "${doc.docid}" был создан`}
          onClose={closeModal2}
        />
      );
    } catch (e) {
      showError2(e, "Не удалось создать документ");
    }
  };

  const getItemLeftAmount = async (itemid) => {
    const data = await api.getItemLeftAmount({
      docid: openedDocument.docid,
      itemid,
    });
    return typeof data === "number" ? data : Number(data) || 0;
  };

  /* ================= закрытие модалок ================= */

  const closeDocumentModal = () => {
    setOpenedDocument(null);
    setOpenedDocumentState({});
    setDocItems([]);
    setSelectedDocItem(null);
    setOpenedDocItem(null);
    setOpenedDocItemState({});
    setDocItemsToDelete([]);
    closeModal();
  };

  const closeDocItemModal = () => {
    setOpenedDocItem(null);
    setOpenedDocItemState({});
    closeModal2();
  };

  return {
    documentsFilters,
    setDocumentsFilters,
    documents,
    setDocuments,
    selectedDocument,
    setSelectedDocument,
    openedDocument,
    openedDocumentState,
    setOpenedDocumentState,
    docItems,
    selectedDocItem,
    setSelectedDocItem,
    openedDocItem,
    setOpenedDocItem,
    openedDocItemState,
    setOpenedDocItemState,

    getAndSavePrintTemplates,
    createDocumentAndOpen,
    openDocument,
    deleteDocument,
    conductDocument,
    unConductDocument,
    updateDocument,
    saveDocItem,
    removeDocItem,
    duplicateDocument,
    getItemLeftAmount,
    createManyDocItems,
    closeDocumentModal,
    closeDocItemModal,
  };
};

export default useDocuments;
